(() => {
  const initialize = (root) => {
    if (!root || root.dataset.jkSearchReady === 'true') return;
    const form = root.querySelector('[data-jk-search-form]');
    const input = form?.querySelector('[name="jk_q"]');
    const clear = root.querySelector('[data-jk-clear-search]');
    const results = root.querySelector('[data-jk-search-results]');
    const status = root.querySelector('[data-jk-search-status]');
    const grid = root.querySelector('[data-jk-search-grid]');
    const more = root.querySelector('[data-jk-search-more]');
    const sort = root.querySelector('[data-jk-search-sort]');
    if (!form || !input || !results || !status || !grid || !more || !sort) return;
    const origin = window.location.origin;
    let collectionUrl;
    try {
      collectionUrl = new URL(root.dataset.collectionUrl, origin);
    } catch {
      return;
    }
    if (collectionUrl.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/jeep-cj-76-86\/?$/i.test(collectionUrl.pathname)) return;
    root.dataset.jkSearchReady = 'true';
    const basePath = collectionUrl.pathname.replace(/\/$/, '');
    const inCollection = (pathname, path = basePath) => pathname.replace(/\/$/, '') === path || pathname.startsWith(`${path}/`);
    const model = 'CJ';
    const initialUrl = new URL(window.location.href);
    const sortValues = {
      'created-descending': 'newest',
      'price-ascending': 'price-ascending',
      'price-descending': 'price-descending',
      'title-ascending': 'title-ascending'
    };
    const restoredSort = sortValues[initialUrl.searchParams.get('sort_by')];
    if (restoredSort && [...sort.options].some((option) => option.value === restoredSort)) sort.value = restoredSort;
    const currency = root.dataset.currency || 'USD';
    const pageSize = 18;
    const catalogPromises = new Map();
    let matches = [];
    let shown = pageSize;
    let revision = 0;
    let activeQuery = '';
    const defaults = {
      from: 'From {price}',
      viewDetails: 'View details',
      viewDetailsLabel: 'View details: {title}',
      soldOut: 'Sold out',
      fitmentCollection: `Jeep ${model} collection`,
      searching: `Searching Jeep ${model} parts…`,
      resultSingle: '{count} part found for “{query}”. Showing {shown}.',
      resultPlural: '{count} parts found for “{query}”. Showing {shown}.',
      noResults: `No parts found for “{query}” with these filters. Try another part name or SKU, or select All ${model} parts and clear the category filters.`,
      showMore: 'Show more parts ({remaining} remaining)',
      failed: 'Search could not load. Please try Search again, or browse the collection below using Filters.'
    };
    let labels = {};
    try {
      const supplied = JSON.parse(root.dataset.jkLabels || '{}');
      if (supplied && typeof supplied === 'object' && !Array.isArray(supplied)) labels = supplied;
    } catch {}
    const label = (key, values = {}) => {
      const template = typeof labels[key] === 'string' && labels[key] ? labels[key] : defaults[key];
      return String(template || '').replace(/\{(\w+)\}/g, (placeholder, name) => Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : placeholder);
    };
    const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const compact = (value) => normalize(value).replace(/\s/g, '');
    const termsFor = (value) => normalize(value).split(/\s+/).filter(Boolean);
    const stringArray = (value) => Array.isArray(value) ? value.map(String) : [];
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const productUrl = (value) => {
      try {
        const url = new URL(value, origin);
        return url.origin === origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname) ? url : null;
      } catch {
        return null;
      }
    };
    const imageUrl = (value) => {
      try {
        const url = new URL(value, origin);
        return /^(https?:)$/.test(url.protocol) ? url.href : null;
      } catch {
        return null;
      }
    };
    const money = (product) => {
      const price = Number(product.price_min ?? product.price);
      let formatted = typeof product.money === 'string' ? product.money.trim() : '';
      if (!formatted || /<[^>]+>/.test(formatted)) {
        try {
          formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(price / 100);
        } catch {
          formatted = `$${(price / 100).toFixed(2)}`;
        }
      }
      return product.price_varies && !/^from\b/i.test(formatted) ? label('from', { price: formatted }) : formatted;
    };
    const typeSelection = (url) => url.searchParams.getAll('filter.p.product_type').flatMap((value) => value.split(',')).map(normalize).filter(Boolean).sort().join('|');
    const navigationTargets = new WeakMap();
    let lastNavigationUrl = '';
    const syncNavigation = () => {
      const current = new URL(window.location.href);
      lastNavigationUrl = current.href;
      root.querySelectorAll('a[data-jk-nav]').forEach((link) => {
        if (!navigationTargets.has(link)) navigationTargets.set(link, link.href);
        const target = new URL(navigationTargets.get(link), origin);
        if (target.origin !== origin || !inCollection(target.pathname)) return;
        const kind = link.dataset.jkNav;
        const url = kind === 'category' ? new URL(current.href) : new URL(target.href);
        if (kind === 'category') {
          url.pathname = target.pathname;
          url.searchParams.delete('filter.p.product_type');
          target.searchParams.getAll('filter.p.product_type').forEach((value) => url.searchParams.append('filter.p.product_type', value));
        }
        url.searchParams.delete('page');
        const query = activeQuery || current.searchParams.get('jk_q');
        if (query) {
          url.searchParams.set('jk_q', query);
          url.hash = results.id || 'mb-jk-search-results';
        } else {
          url.searchParams.delete('jk_q');
          url.hash = 'mb-jk-parts';
        }
        link.href = url.href;
        if (kind !== 'category') return;
        const active = target.pathname.replace(/\/$/, '') === current.pathname.replace(/\/$/, '') && typeSelection(target) === typeSelection(current);
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };
    const nativeFilterKeys = new Set(['filter.p.product_type', 'filter.v.price.gte', 'filter.v.price.lte', 'filter.v.availability']);
    const catalogPageUrl = (href, scope, filters = []) => {
      const url = new URL(href, scope.href);
      if (url.origin !== origin || url.pathname.replace(/\/$/, '') !== scope.pathname.replace(/\/$/, '')) throw new Error('Unexpected collection page');
      const page = url.searchParams.get('page');
      if (page && (!/^\d+$/.test(page) || Number(page) < 1)) throw new Error('Invalid collection page');
      url.search = '';
      url.searchParams.set('view', 'motobilt-finder-data');
      url.searchParams.set('sort_by', 'created-descending');
      if (page && page !== '1') url.searchParams.set('page', page);
      filters.forEach(([name, value]) => url.searchParams.append(name, value));
      const previewTheme = initialUrl.searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      url.hash = '';
      return url;
    };
    const loadCatalog = () => {
      const current = new URL(window.location.href);
      const scope = new URL(collectionUrl.href);
      if (current.origin === origin && inCollection(current.pathname)) scope.pathname = current.pathname;
      const filters = [...current.searchParams.entries()].filter(([name, value]) => nativeFilterKeys.has(name) && value !== '');
      const key = JSON.stringify([scope.pathname, [...filters].sort(([nameA, valueA], [nameB, valueB]) => nameA.localeCompare(nameB) || valueA.localeCompare(valueB))]);
      if (catalogPromises.has(key)) return catalogPromises.get(key);
      const promise = (async () => {
        let next = catalogPageUrl(scope.href, scope, filters);
        const visited = new Set();
        const products = [];
        const ids = new Set();
        while (next) {
          if (visited.has(next.href) || visited.size >= 40) throw new Error('Collection pagination unavailable');
          visited.add(next.href);
          const controller = new AbortController();
          const timeout = window.setTimeout(() => controller.abort(), 20000);
          try {
            const response = await fetch(next.href, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: controller.signal });
            if (!response.ok) throw new Error('Collection search unavailable');
            catalogPageUrl(response.url || next.href, scope, filters);
            const data = await response.json();
            if (!Array.isArray(data.products)) throw new Error('Invalid collection data');
            data.products.forEach((product) => {
              const url = productUrl(product.url);
              if (!product.id || ids.has(String(product.id)) || !url || !Number.isFinite(Number(product.price_min ?? product.price))) return;
              ids.add(String(product.id));
              const tags = stringArray(product.tags);
              const skus = stringArray(product.skus);
              products.push({ ...product, handle: url.pathname.replace(/\/$/, '').split('/').pop(), url: url.href, tags, skus, position: products.length, searchText: normalize([product.title, product.type, ...skus].join(' ')), normalizedSkus: skus.map(compact) });
            });
            next = data.next ? catalogPageUrl(data.next, scope, filters) : null;
          } finally {
            window.clearTimeout(timeout);
          }
        }
        return products;
      })().catch((error) => {
        catalogPromises.delete(key);
        throw error;
      });
      catalogPromises.set(key, promise);
      return promise;
    };
    const createCard = (product) => {
      const card = element('article', 'mb-jk-search-card');
      const media = element('a', 'mb-jk-search-card__media');
      media.href = product.url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      const source = imageUrl(product.image?.url);
      if (source) {
        const image = element('img');
        image.src = source;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = Number(product.image?.width) || 600;
        image.height = Number(product.image?.height) || 600;
        media.append(image);
      }
      const content = element('div', 'mb-jk-search-card__content');
      const fitment = label('fitmentCollection');
      content.append(element('p', 'mb-jk-search-card__fitment', fitment));
      const heading = element('h3', 'mb-jk-search-card__title');
      const title = element('a', '', product.title);
      title.href = product.url;
      heading.append(title);
      content.append(heading, element('p', 'mb-jk-search-card__price', money(product)));
      if (!product.available) content.append(element('p', 'mb-jk-search-card__stock', label('soldOut')));
      const action = element('a', 'button button--styled mb-tape-surface mb-build-product-cta mb-jk-card-cta');
      action.href = product.url;
      action.setAttribute('aria-label', label('viewDetailsLabel', { title: product.title }));
      const tape = element('span', 'button_flag');
      tape.setAttribute('aria-hidden', 'true');
      const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      arrow.setAttribute('viewBox', '0 0 24 24');
      arrow.setAttribute('width', '24');
      arrow.setAttribute('height', '24');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.setAttribute('focusable', 'false');
      arrow.classList.add('mb-jk-card-arrow');
      const arrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      arrowPath.setAttribute('d', 'M5 12h14m-6-6 6 6-6 6');
      arrowPath.setAttribute('fill', 'none');
      arrowPath.setAttribute('stroke', 'currentColor');
      arrowPath.setAttribute('stroke-width', '2');
      arrowPath.setAttribute('stroke-linecap', 'square');
      arrowPath.setAttribute('stroke-linejoin', 'miter');
      arrow.append(arrowPath);
      action.append(tape, element('span', 'button_label', label('viewDetails')), arrow);
      content.append(action);
      card.append(media, content);
      return card;
    };
    const render = () => {
      const mode = sort.value;
      matches.sort((a, b) => {
        if (a.exact !== b.exact) return Number(b.exact) - Number(a.exact);
        if (mode === 'price-ascending' || mode === 'price-asc') return Number(a.price_min ?? a.price) - Number(b.price_min ?? b.price) || a.position - b.position;
        if (mode === 'price-descending' || mode === 'price-desc') return Number(b.price_min ?? b.price) - Number(a.price_min ?? a.price) || a.position - b.position;
        if (mode === 'title-ascending' || mode === 'title-asc') return String(a.title).localeCompare(String(b.title)) || a.position - b.position;
        return a.position - b.position;
      });
      const fragment = document.createDocumentFragment();
      matches.slice(0, shown).forEach((product) => fragment.append(createCard(product)));
      grid.replaceChildren(fragment);
      const count = matches.length;
      const statusLabel = count ? (count === 1 ? 'resultSingle' : 'resultPlural') : 'noResults';
      status.textContent = label(statusLabel, { count, query: activeQuery, shown: Math.min(shown, count) });
      more.hidden = shown >= count;
      if (!more.hidden) (more.querySelector('.button_label') || more).textContent = label('showMore', { remaining: count - shown });
    };
    const clearSearch = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('jk_q');
      url.hash = 'mb-jk-parts';
      window.location.assign(url.href);
    };
    const runSearch = async (query, focusResults) => {
      activeQuery = String(query || '').trim().slice(0, 200);
      input.value = activeQuery;
      if (!activeQuery) {
        clearSearch();
        return;
      }
      const currentRevision = ++revision;
      const url = new URL(window.location.href);
      if (activeQuery) url.searchParams.set('jk_q', activeQuery);
      else url.searchParams.delete('jk_q');
      url.searchParams.delete('page');
      window.history.replaceState(window.history.state, '', url.href);
      syncNavigation();
      root.classList.add('is-searching');
      results.hidden = false;
      results.setAttribute('aria-busy', 'true');
      status.textContent = label('searching');
      grid.replaceChildren();
      more.hidden = true;
      sort.disabled = true;
      if (clear) clear.hidden = !activeQuery;
      try {
        const products = await loadCatalog();
        if (currentRevision !== revision || !root.isConnected) return;
        const terms = termsFor(activeQuery);
        const exactQuery = compact(activeQuery);
        matches = products.filter((product) => {
          const matchesText = !terms.length || terms.every((term) => product.searchText.includes(term)) || product.normalizedSkus.some((sku) => sku.startsWith(exactQuery));
          return matchesText;
        }).map((product) => ({ ...product, exact: Boolean(exactQuery) && product.normalizedSkus.includes(exactQuery) }));
        shown = pageSize;
        sort.disabled = false;
        render();
        if (focusResults) {
          status.tabIndex = -1;
          status.focus({ preventScroll: true });
          results.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      } catch {
        if (currentRevision !== revision || !root.isConnected) return;
        root.classList.remove('is-searching');
        status.textContent = label('failed');
      } finally {
        if (currentRevision === revision) results.setAttribute('aria-busy', 'false');
      }
    };
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      runSearch(input.value, true);
    });
    clear?.addEventListener('click', (event) => {
      event.preventDefault();
      clearSearch();
    });
    more.addEventListener('click', () => {
      const firstNewIndex = shown;
      shown += pageSize;
      render();
      const nextHeadingLink = grid.children[firstNewIndex]?.querySelector('h3 a');
      nextHeadingLink?.focus();
    });
    sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      const selectedSort = Object.keys(sortValues).find((key) => sortValues[key] === sort.value);
      if (selectedSort) url.searchParams.set('sort_by', selectedSort);
      url.searchParams.delete('page');
      window.history.replaceState(window.history.state, '', url.href);
      syncNavigation();
      shown = pageSize;
      render();
    });
    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[data-vehicle-scroll-target="mb-jk-parts"][href="#mb-jk-parts"]');
      const target = root.querySelector('#mb-jk-parts');
      if (!link || !root.contains(link) || !target) return;
      event.preventDefault();
      event.stopPropagation();
      const url = new URL(window.location.href);
      url.hash = 'mb-jk-parts';
      window.history.replaceState(window.history.state, '', url.href);
      target.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, true);
    root.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('a[data-jk-nav]')) syncNavigation();
    });
    const navigationObserver = new MutationObserver(() => {
      if (!root.isConnected) {
        navigationObserver.disconnect();
        return;
      }
      if (window.location.href !== lastNavigationUrl) syncNavigation();
    });
    navigationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('pageshow', () => {
      if (root.isConnected) syncNavigation();
    });
    window.addEventListener('popstate', () => {
      if (root.isConnected) window.location.reload();
    });
    syncNavigation();
    const restoredQuery = initialUrl.searchParams.get('jk_q');
    if (restoredQuery?.trim()) runSearch(restoredQuery, false);
    else if (clear) clear.hidden = true;
  };
  const initializeAll = () => document.querySelectorAll('.mb-cj-landing').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();

